/**
 * Canonical Finnish localities (and common exonyms) with display names per locale.
 * Used for URL routing, market header, and processor normalization of job location_* arrays.
 *
 * Each key is a recognized variant; values are the correct label for en / fi / sv for that place.
 * Remote / Distans scope labels live in url-constants (REMOTE_AND_SCOPE_LOCALE_NAMES), not here.
 */

export type FinnishLocalityLocaleRow = { fi: string; sv: string; en: string };

/**
 * Large Finnish localities and Swedish/Finnish exonyms. Keys include alternate spellings.
 */
export const FINNISH_LOCALITY_LOCALE_NAMES: Record<string, FinnishLocalityLocaleRow> = {
  Helsinki: { fi: 'Helsinki', sv: 'Helsingfors', en: 'Helsinki' },
  /** Startup campus in Helsinki; often appears as a raw location string */
  Maria01: { fi: 'Helsinki', sv: 'Helsingfors', en: 'Helsinki' },
  Helsingfors: { fi: 'Helsinki', sv: 'Helsingfors', en: 'Helsinki' },
  Tampere: { fi: 'Tampere', sv: 'Tammerfors', en: 'Tampere' },
  Tammerfors: { fi: 'Tampere', sv: 'Tammerfors', en: 'Tampere' },
  Turku: { fi: 'Turku', sv: 'Åbo', en: 'Turku' },
  Åbo: { fi: 'Turku', sv: 'Åbo', en: 'Turku' },
  Abo: { fi: 'Turku', sv: 'Åbo', en: 'Turku' },
  Oulu: { fi: 'Oulu', sv: 'Uleåborg', en: 'Oulu' },
  'Uleåborg': { fi: 'Oulu', sv: 'Uleåborg', en: 'Oulu' },
  Uleaborg: { fi: 'Oulu', sv: 'Uleåborg', en: 'Oulu' },
  Espoo: { fi: 'Espoo', sv: 'Esbo', en: 'Espoo' },
  Esbo: { fi: 'Espoo', sv: 'Esbo', en: 'Espoo' },
  Vantaa: { fi: 'Vantaa', sv: 'Vanda', en: 'Vantaa' },
  Vanda: { fi: 'Vantaa', sv: 'Vanda', en: 'Vantaa' },
  Vaasa: { fi: 'Vaasa', sv: 'Vasa', en: 'Vaasa' },
  Vasa: { fi: 'Vaasa', sv: 'Vasa', en: 'Vaasa' },
  Lahti: { fi: 'Lahti', sv: 'Lahtis', en: 'Lahti' },
  Lahtis: { fi: 'Lahti', sv: 'Lahtis', en: 'Lahti' },
  Kuopio: { fi: 'Kuopio', sv: 'Kuopio', en: 'Kuopio' },
  'Jyväskylä': { fi: 'Jyväskylä', sv: 'Jyväskylä', en: 'Jyväskylä' },
  Jyvaskyla: { fi: 'Jyväskylä', sv: 'Jyväskylä', en: 'Jyväskylä' },
  Pori: { fi: 'Pori', sv: 'Björneborg', en: 'Pori' },
  'Björneborg': { fi: 'Pori', sv: 'Björneborg', en: 'Pori' },
  Bjorneborg: { fi: 'Pori', sv: 'Björneborg', en: 'Pori' },
  Lappeenranta: { fi: 'Lappeenranta', sv: 'Villmanstrand', en: 'Lappeenranta' },
  Villmanstrand: { fi: 'Lappeenranta', sv: 'Villmanstrand', en: 'Lappeenranta' },
  Kotka: { fi: 'Kotka', sv: 'Kotka', en: 'Kotka' },
  Joensuu: { fi: 'Joensuu', sv: 'Joensuu', en: 'Joensuu' },
  Rovaniemi: { fi: 'Rovaniemi', sv: 'Rovaniemi', en: 'Rovaniemi' },
  'Seinäjoki': { fi: 'Seinäjoki', sv: 'Seinäjoki', en: 'Seinäjoki' },
  Mikkeli: { fi: 'Mikkeli', sv: 'S:t Michel', en: 'Mikkeli' },
  'S:t Michel': { fi: 'Mikkeli', sv: 'S:t Michel', en: 'Mikkeli' },
  /** Colon-less variant: URL slugs split on "_" so "st_michel_finland" reconstructs to "st michel", never "s:t michel" */
  'St Michel': { fi: 'Mikkeli', sv: 'S:t Michel', en: 'Mikkeli' },
  'Hyvinkää': { fi: 'Hyvinkää', sv: 'Hyvinge', en: 'Hyvinkää' },
  Hyvinge: { fi: 'Hyvinkää', sv: 'Hyvinge', en: 'Hyvinkää' },
  'Hämeenlinna': { fi: 'Hämeenlinna', sv: 'Tavastehus', en: 'Hämeenlinna' },
  Tavastehus: { fi: 'Hämeenlinna', sv: 'Tavastehus', en: 'Hämeenlinna' },
  Kouvola: { fi: 'Kouvola', sv: 'Kouvola', en: 'Kouvola' },
  Porvoo: { fi: 'Porvoo', sv: 'Borgå', en: 'Porvoo' },
  'Borgå': { fi: 'Porvoo', sv: 'Borgå', en: 'Porvoo' },
  Kokkola: { fi: 'Kokkola', sv: 'Karleby', en: 'Kokkola' },
  Karleby: { fi: 'Kokkola', sv: 'Karleby', en: 'Kokkola' },
  Rauma: { fi: 'Rauma', sv: 'Raumo', en: 'Rauma' },
  Raumo: { fi: 'Rauma', sv: 'Raumo', en: 'Rauma' },
  Hanko: { fi: 'Hanko', sv: 'Hangö', en: 'Hanko' },
  'Hangö': { fi: 'Hanko', sv: 'Hangö', en: 'Hanko' },
  Kemi: { fi: 'Kemi', sv: 'Kemi', en: 'Kemi' },
  // TEC-482 spot-check: real GeoNames data for these 4 has the canonical `name` field set to the
  // Swedish exonym rather than Finnish (confirmed against alternatenames), same pattern as the
  // Mikkeli/S:t Michel gap this file already fixes, found while auditing the other legacy
  // location dictionaries for the same class of gap.
  'Pietarsaari': { fi: 'Pietarsaari', sv: 'Jakobstad', en: 'Pietarsaari' },
  Jakobstad: { fi: 'Pietarsaari', sv: 'Jakobstad', en: 'Pietarsaari' },
  'Tammisaari': { fi: 'Tammisaari', sv: 'Ekenäs', en: 'Tammisaari' },
  'Ekenäs': { fi: 'Tammisaari', sv: 'Ekenäs', en: 'Tammisaari' },
  'Uusikaarlepyy': { fi: 'Uusikaarlepyy', sv: 'Nykarleby', en: 'Uusikaarlepyy' },
  Nykarleby: { fi: 'Uusikaarlepyy', sv: 'Nykarleby', en: 'Uusikaarlepyy' },
  'Kristiinankaupunki': { fi: 'Kristiinankaupunki', sv: 'Kristinestad', en: 'Kristiinankaupunki' },
  Kristinestad: { fi: 'Kristiinankaupunki', sv: 'Kristinestad', en: 'Kristiinankaupunki' },
};

const LOWER_VARIANT_TO_ROW: Map<string, FinnishLocalityLocaleRow> = new Map();
for (const [key, row] of Object.entries(FINNISH_LOCALITY_LOCALE_NAMES)) {
  LOWER_VARIANT_TO_ROW.set(key.toLowerCase(), row);
}

/**
 * If the string is "City, Country" and City is a known Finnish locality variant,
 * replace City with the correct name for the target locale. Otherwise returns the input unchanged.
 * Does not modify "Remote - …" / "Distans - …" style strings (no comma-based city segment).
 */
export function normalizeFinnishLocalityInLocationString(
  loc: string,
  locale: 'en' | 'fi' | 'sv',
): string {
  if (typeof loc !== 'string') return loc;
  const trimmed = loc.trim();
  if (!trimmed.includes(',')) return loc;
  if (/^(?:remote|distans|etätyö|etatyö)\s*-/i.test(trimmed)) return loc;

  const commaIdx = trimmed.indexOf(',');
  const city = trimmed.slice(0, commaIdx).trim();
  const countryAndRest = trimmed.slice(commaIdx + 1).trim();
  const row = LOWER_VARIANT_TO_ROW.get(city.toLowerCase());
  if (!row) return loc;

  const localizedCity = row[locale];
  return `${localizedCity}, ${countryAndRest}`;
}

export interface JobWithLocaleLocationArrays {
  location_en?: string[];
  location_fi?: string[];
  location_sv?: string[];
}

/**
 * Normalizes known Finnish locality names in location_en / location_fi / location_sv per locale.
 * Call after {@link import('./normalize-location-country-names').normalizeLocationCountryNames}.
 */
export function normalizeFinnishLocalityNamesInJobLocations(job: JobWithLocaleLocationArrays): void {
  const fields: Array<{ key: 'location_en' | 'location_fi' | 'location_sv'; locale: 'en' | 'fi' | 'sv' }> = [
    { key: 'location_en', locale: 'en' },
    { key: 'location_fi', locale: 'fi' },
    { key: 'location_sv', locale: 'sv' },
  ];
  for (const { key, locale } of fields) {
    const arr = job[key];
    if (!arr || !Array.isArray(arr)) continue;
    job[key] = arr.map((entry) =>
      typeof entry === 'string' ? normalizeFinnishLocalityInLocationString(entry, locale) : entry,
    );
  }
}
